/**
 * AccountManagement.tsx — Super-admin account management.
 * Tab 1: All Accounts   — full list with all actions
 * Tab 2: Expired         — expired trials + payment failures, bulk actions
 * Tab 3: Suspended/Closed — suspended and closed accounts
 */

import React, { useCallback, useEffect, useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import BusinessIcon from '@mui/icons-material/Business';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import EmailIcon from '@mui/icons-material/Email';
import LockResetIcon from '@mui/icons-material/LockReset';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import axios from 'axios';
import API_BASE from '../../config/api';
import { useAuth } from '../../context/AuthContext';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Account {
  id:                   string;
  name:                 string;
  email:                string;
  organisation:         string;
  plan:                 string;
  status:               string;
  billing_status:       string;
  billing_plan:         string;
  mrr_cents:            number;
  trial_end:            string | null;
  cancel_at_period_end: boolean;
  users_count:          number;
  created_at:           string;
  updated_at:           string;
}

interface ExpiredAccount {
  id:             string;
  name:           string;
  email:          string;
  plan:           string;
  billing_status: string;
  trial_end:      string | null;
  paused_since:   string | null;
  paused_days:    number;
  reason:         'trial_expired' | 'payment_failed';
  users_count:    number;
  users:          { id: string; email: string; full_name: string }[];
}

interface ResetResult {
  email:         string;
  full_name:     string;
  temp_password: string;
}

interface RecycleBinAccount {
  id:             string;
  name:           string;
  email:          string;
  organisation:   string;
  status:         string;
  deleted_at:     string;
  days_remaining: number;
}

type DialogState =
  | null
  | { type: 'confirm';         title: string; body: string; confirmLabel: string; confirmColor?: 'error' | 'warning' | 'primary'; onConfirm: () => void }
  | { type: 'extend_trial';    accounts: Account[]; bulk: boolean }
  | { type: 'send_email';      accountIds: string[]; accountNames: string }
  | { type: 'reset_passwords'; accountId: string; accountName: string; step: 'confirm' | 'loading' | 'results'; results: ResetResult[] };

// ── Email template options ────────────────────────────────────────────────────

const EMAIL_TEMPLATES = [
  { value: 'trial_ending',      label: 'Trial Ending (3 days)',   preview: "Your Shield trial ends soon. Everything you've set up is safe. Add a payment method to keep your account active — it takes less than a minute." },
  { value: 'trial_expired',     label: 'Trial Expired',           preview: "Your free trial has ended. Your data is preserved for 30 days. Reactivate anytime to pick up exactly where you left off." },
  { value: 'payment_failed',    label: 'Payment Failed',          preview: "We couldn't process your last payment. Your account is still active for now. Please update your payment method to avoid any interruption." },
  { value: 'account_suspended', label: 'Account Suspended',       preview: "Your account has been temporarily paused. Your data is safe. Contact us at support@shield.io to reactivate." },
  { value: 'new_trial',         label: 'Trial Extended',          preview: "Your free trial has been extended. Enjoy the extra time to explore Shield." },
  { value: 'custom',            label: 'Custom Message',          preview: '' },
];

// ── Chips ─────────────────────────────────────────────────────────────────────

function AccountStatusChip({ status }: { status: string }) {
  const cfg: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'default' | 'primary' | 'info' }> = {
    trial:      { label: 'Trial',      color: 'primary'  },
    active:     { label: 'Active',     color: 'success'  },
    past_due:   { label: 'Past Due',   color: 'warning'  },
    suspended:  { label: 'Suspended',  color: 'warning'  },
    terminated: { label: 'Terminated', color: 'error'    },
    // legacy values
    closed:     { label: 'Terminated', color: 'error'    },
    paused:     { label: 'Trial',      color: 'primary'  },
  };
  const { label, color } = cfg[status] ?? { label: status, color: 'default' as const };
  return <Chip label={label} size="small" color={color as any} sx={{ textTransform: 'capitalize' }} />;
}

function BillingChip({ status }: { status: string }) {
  const cfg: Record<string, { label: string; color: 'primary' | 'success' | 'error' | 'warning' | 'default' }> = {
    trialing:  { label: 'Trial',     color: 'primary' },
    active:    { label: 'Active',    color: 'success' },
    past_due:  { label: 'Past Due',  color: 'error' },
    unpaid:    { label: 'Unpaid',    color: 'error' },
    canceled:  { label: 'Canceled',  color: 'default' },
    suspended: { label: 'Suspended', color: 'warning' },
    none:      { label: 'None',      color: 'default' },
  };
  const { label, color } = cfg[status] ?? { label: status, color: 'default' as const };
  return <Chip label={label} size="small" color={color} variant="outlined" sx={{ fontWeight: 600 }} />;
}

function ReasonChip({ reason }: { reason: 'trial_expired' | 'payment_failed' }) {
  return reason === 'trial_expired'
    ? <Chip label="Trial Ended"    size="small" sx={{ bgcolor: 'rgba(255,152,0,0.15)',  color: '#ff9800',  fontWeight: 600 }} />
    : <Chip label="Payment Failed" size="small" sx={{ bgcolor: 'rgba(239,35,60,0.12)', color: 'error.main', fontWeight: 600 }} />;
}

// ── Summary card ──────────────────────────────────────────────────────────────

function KpiCard({ icon, label, value, color = 'primary.main', alert = false }: {
  icon: React.ReactNode; label: string; value: string | number; color?: string; alert?: boolean;
}) {
  return (
    <Card sx={{
      borderRadius: 2,
      border: alert ? '1px solid rgba(239,35,60,0.4)' : '1px solid rgba(255,255,255,0.06)',
      bgcolor: alert ? 'rgba(239,35,60,0.06)' : 'background.paper',
    }}>
      <CardContent sx={{ pb: '12px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          <Box sx={{ color }}>{icon}</Box>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
        </Box>
        <Typography variant="h5" fontWeight={700} sx={{ color: alert ? 'error.main' : 'text.primary' }}>{value}</Typography>
      </CardContent>
    </Card>
  );
}

// ── UUID copy badge ───────────────────────────────────────────────────────────

function CopyUuid({ id }: { id: string }) {
  const [copied, setCopied] = useState(false);
  function handleCopy() {
    navigator.clipboard.writeText(id);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mt: 0.25 }}>
      <Typography
        variant="caption"
        sx={{ fontFamily: 'monospace', color: 'text.disabled', fontSize: '0.68rem' }}
      >
        {id}
      </Typography>
      <Tooltip title={copied ? 'Copied!' : 'Copy ID'} placement="right">
        <IconButton size="small" onClick={handleCopy} sx={{ p: 0.25, opacity: 0.45, '&:hover': { opacity: 1 } }}>
          <ContentCopyIcon sx={{ fontSize: 11 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

// ── Row action menu ───────────────────────────────────────────────────────────

function AccountRowMenu({ account, onAction }: {
  account: Account;
  onAction: (action: string) => void;
}) {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const s = account.status;

  const close = (action?: string) => { setAnchor(null); if (action) onAction(action); };

  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)}><MoreVertIcon fontSize="small" /></IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {/* Status-dependent primary actions — strictly one opposite per status */}
        {s === 'active'                        && <MenuItem onClick={() => close('suspend')}>Suspend</MenuItem>}
        {(s === 'suspended' || s === 'paused') && <MenuItem onClick={() => close('reactivate')} sx={{ color: 'success.main' }}>Reactivate</MenuItem>}
        {s === 'closed'                        && <MenuItem onClick={() => close('reopen')}     sx={{ color: 'success.main' }}>Reopen</MenuItem>}
        {s !== 'closed'                        && <MenuItem onClick={() => close('close')}      sx={{ color: 'error.main'   }}>Close Account</MenuItem>}
        {/* Always visible */}
        <MenuItem onClick={() => close('send_email')}><EmailIcon     fontSize="small" sx={{ mr: 1 }} />Send Email</MenuItem>
        <MenuItem onClick={() => close('reset_passwords')}><LockResetIcon fontSize="small" sx={{ mr: 1 }} />Reset User Passwords</MenuItem>
      </Menu>
    </>
  );
}

// ── Shared table columns ──────────────────────────────────────────────────────

function fmtDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString() : '—';
}

function fmtMRR(cents: number): string {
  return cents > 0 ? `$${(cents / 100).toFixed(0)}/mo` : '—';
}

// ── Dialogs ───────────────────────────────────────────────────────────────────

function ConfirmDlg({ d, onClose }: { d: Extract<DialogState, { type: 'confirm' }>; onClose: () => void }) {
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{d.title}</DialogTitle>
      <DialogContent><Typography variant="body2">{d.body}</Typography></DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color={d.confirmColor ?? 'primary'} onClick={() => { d.onConfirm(); onClose(); }}>
          {d.confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ExtendTrialDlg({ d, onClose, onDone }: {
  d: Extract<DialogState, { type: 'extend_trial' }>;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const [days, setDays]         = useState(14);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  async function submit() {
    if (days < 1) return;
    setLoading(true); setError('');
    try {
      for (const acct of d.accounts) {
        await axios.patch(`${API_BASE}/admin/accounts/${acct.id}/trial`, { trial_days: days });
      }
      const names = d.accounts.map((a) => a.name).join(', ');
      onDone(`Trial extended by ${days} days for: ${names}`);
      onClose();
    } catch {
      setError('Failed to extend trial.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Extend Trial{d.bulk ? ` (${d.accounts.length} accounts)` : ` — ${d.accounts[0]?.name}`}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography variant="body2" color="text.secondary" mb={2}>
          {d.bulk
            ? `New trial end = today + N days, applied to all ${d.accounts.length} selected accounts.`
            : 'New trial end = today + N days. An email will be sent to all account users.'}
        </Typography>
        <TextField
          label="Days to add"
          type="number"
          fullWidth
          value={days}
          onChange={(e) => setDays(Math.max(1, parseInt(e.target.value) || 1))}
          inputProps={{ min: 1, max: 365 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={loading || days < 1}>
          {loading ? <CircularProgress size={18} color="inherit" /> : `Extend ${days} day${days !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SendEmailDlg({ d, onClose, onDone }: {
  d: Extract<DialogState, { type: 'send_email' }>;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const [template, setTemplate]   = useState('trial_expired');
  const [message, setMessage]     = useState(EMAIL_TEMPLATES[1].preview);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');

  function handleTemplateChange(val: string) {
    setTemplate(val);
    const t = EMAIL_TEMPLATES.find((t) => t.value === val);
    setMessage(t?.preview ?? '');
  }

  async function submit() {
    setLoading(true); setError('');
    try {
      const { data } = await axios.post(`${API_BASE}/admin/accounts/notify`, {
        account_ids: d.accountIds,
        template,
        message,
      });
      onDone(`Sent ${data.sent} email(s) to ${data.total_users} user(s).`);
      onClose();
    } catch {
      setError('Failed to send emails.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send Email — {d.accountNames}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography variant="body2" color="text.secondary" mb={2}>
          Email will be sent to all users of the selected account(s).
        </Typography>
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Template</InputLabel>
          <Select value={template} label="Template" onChange={(e) => handleTemplateChange(e.target.value)}>
            {EMAIL_TEMPLATES.map((t) => (
              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Message / preview"
          multiline
          rows={5}
          fullWidth
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          helperText="Editable — this text is shown to the user (or appended for custom messages)."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <EmailIcon />}
          onClick={submit}
          disabled={loading}
        >
          Send
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ResetPasswordsDlg({ d, onClose, onAction }: {
  d: Extract<DialogState, { type: 'reset_passwords' }>;
  onClose: () => void;
  onAction: (step: 'loading' | 'results', results?: ResetResult[]) => void;
}) {
  async function doReset() {
    onAction('loading');
    try {
      const { data } = await axios.post(`${API_BASE}/admin/accounts/${d.accountId}/reset-passwords`);
      onAction('results', data.results);
    } catch {
      onAction('results', []);
    }
  }

  if (d.step === 'confirm') {
    return (
      <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>Reset All Passwords — {d.accountName}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will generate a new temporary password for every active user of this account.
            Each user will receive an email with their new password immediately.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={doReset}>Reset Passwords</Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (d.step === 'loading') {
    return (
      <Dialog open maxWidth="xs" fullWidth>
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" mt={2}>Resetting passwords…</Typography>
        </DialogContent>
      </Dialog>
    );
  }

  // results
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Passwords Reset — {d.accountName}</DialogTitle>
      <DialogContent>
        {d.results.length === 0 ? (
          <Alert severity="error">Reset failed or no active users found.</Alert>
        ) : (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              {d.results.length} password{d.results.length !== 1 ? 's' : ''} reset. Each user received an email.
            </Alert>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              Temporary passwords (shown once — store securely):
            </Typography>
            <Stack spacing={1}>
              {d.results.map((r) => (
                <Box key={r.email} sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(0,180,216,0.06)', borderRadius: 1, p: 1.5 }}>
                  <Box flex={1}>
                    <Typography variant="body2" fontWeight={600}>{r.full_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{r.email}</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'primary.main', fontWeight: 700 }}>
                    {r.temp_password}
                  </Typography>
                  <Tooltip title="Copy password">
                    <IconButton size="small" onClick={() => navigator.clipboard.writeText(r.temp_password)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function AccountManagement() {
  const { user: authUser } = useAuth();
  const isSuperAdmin = authUser?.role === 'super_admin';

  const [tab, setTab]           = useState(0);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [expired, setExpired]   = useState<ExpiredAccount[]>([]);
  const [recycle, setRecycle]   = useState<RecycleBinAccount[]>([]);
  const [summary, setSummary]   = useState({ total_mrr_cents: 0, trial_count: 0, past_due_count: 0 });
  const [loading, setLoading]   = useState(true);
  const [expLoading, setExpLoading]   = useState(false);
  const [recLoading, setRecLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [msg, setMsg]           = useState('');
  const [search, setSearch]     = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [dialog, setDialog]     = useState<DialogState>(null);
  const [scanUsageToday, setScanUsageToday] = useState<Record<string, number>>({});

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/admin/accounts`);
      const accts: Account[] = Array.isArray(data) ? data : (data.accounts ?? []);
      setAccounts(accts);
      if (!Array.isArray(data)) {
        setSummary({
          total_mrr_cents: data.total_mrr_cents ?? 0,
          trial_count:     data.trial_count     ?? 0,
          past_due_count:  data.past_due_count  ?? 0,
        });
      }
      // Fetch today's scan count per customer (fire-and-forget, non-blocking)
      Promise.allSettled(
        accts.map((a) => axios.get(`${API_BASE}/v1/scans/usage/${a.id}`))
      ).then((results) => {
        const usageMap: Record<string, number> = {};
        results.forEach((r, i) => {
          if (r.status === 'fulfilled') {
            const d = r.value.data;
            usageMap[accts[i].id] = (d.today_manual ?? 0) + (d.today_nhi ?? 0);
          }
        });
        setScanUsageToday(usageMap);
      });
    } catch { setError('Failed to load accounts.'); }
    finally   { setLoading(false); }
  }, []);

  const fetchExpired = useCallback(async () => {
    setExpLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/admin/accounts/expired`);
      setExpired(data ?? []);
    } catch { /* silently fail — shown in tab */ }
    finally { setExpLoading(false); }
  }, []);

  const fetchRecycle = useCallback(async () => {
    setRecLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/admin/accounts/recycle-bin`);
      setRecycle(data ?? []);
    } catch { /* silently fail */ }
    finally { setRecLoading(false); }
  }, []);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);
  useEffect(() => { if (tab === 1) fetchExpired(); }, [tab, fetchExpired]);
  useEffect(() => { if (tab === 3) fetchRecycle(); }, [tab, fetchRecycle]);

  // ── Account actions ────────────────────────────────────────────────────────

  async function doAction(action: string, id: string, name: string) {
    try {
      if (action === 'suspend')    await axios.post(`${API_BASE}/admin/accounts/${id}/suspend`);
      if (action === 'reactivate') await axios.post(`${API_BASE}/admin/accounts/${id}/reactivate`);
      if (action === 'close')      await axios.post(`${API_BASE}/admin/accounts/${id}/terminate`);
      if (action === 'reopen')     await axios.post(`${API_BASE}/admin/accounts/${id}/reopen`);
      if (action === 'terminate')  await axios.post(`${API_BASE}/admin/accounts/${id}/terminate`);
      if (action === 'restore')    await axios.post(`${API_BASE}/admin/accounts/${id}/restore`);
      setMsg(`${name}: ${action} completed.`);
      fetchAccounts();
      if (tab === 1) fetchExpired();
      if (tab === 3) fetchRecycle();
    } catch (err: any) {
      setMsg(err.response?.data?.detail ?? 'Action failed.');
    }
  }

  function handleRowAction(action: string, account: Account) {
    const id   = account.id;
    const name = account.name;

    if (action === 'suspend') {
      setDialog({ type: 'confirm', title: `Suspend "${name}"?`, body: 'This will suspend all users under this account.', confirmLabel: 'Suspend', confirmColor: 'warning', onConfirm: () => doAction('suspend', id, name) });
    } else if (action === 'reactivate') {
      setDialog({ type: 'confirm', title: `Reactivate "${name}"?`, body: 'Restore access for all users under this account.', confirmLabel: 'Reactivate', onConfirm: () => doAction('reactivate', id, name) });
    } else if (action === 'close') {
      setDialog({ type: 'confirm', title: `Close "${name}"?`, body: 'This closes the account and signs out all users. It can be reopened later.', confirmLabel: 'Close Account', confirmColor: 'error', onConfirm: () => doAction('close', id, name) });
    } else if (action === 'reopen') {
      setDialog({ type: 'confirm', title: `Reopen "${name}"?`, body: 'Restore full access for all users.', confirmLabel: 'Reopen', onConfirm: () => doAction('reopen', id, name) });
    } else if (action === 'extend_trial') {
      setDialog({ type: 'extend_trial', accounts: [account], bulk: false });
    } else if (action === 'cancel_trial') {
      setDialog({ type: 'confirm', title: `Cancel trial for "${name}"?`, body: "This immediately expires the trial. The account will be flagged as expired on next login.", confirmLabel: 'Cancel Trial', confirmColor: 'warning', onConfirm: async () => { await axios.delete(`${API_BASE}/admin/accounts/${id}/trial`); setMsg(`Trial cancelled for ${name}.`); fetchAccounts(); } });
    } else if (action === 'send_email') {
      setDialog({ type: 'send_email', accountIds: [id], accountNames: name });
    } else if (action === 'reset_passwords') {
      setDialog({ type: 'reset_passwords', accountId: id, accountName: name, step: 'confirm', results: [] });
    }
  }

  // ── Selection helpers ──────────────────────────────────────────────────────

  function toggleSelect(id: string) {
    setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  function toggleSelectAll(rows: { id: string }[]) {
    setSelected((prev) => prev.size === rows.length ? new Set() : new Set(rows.map((r) => r.id)));
  }

  // ── Filtered views ─────────────────────────────────────────────────────────

  const filteredAll = accounts.filter((a) =>
    !search || a.name.toLowerCase().includes(search.toLowerCase()) || a.email.toLowerCase().includes(search.toLowerCase())
  );

  const filteredSuspended = accounts.filter((a) =>
    ['suspended', 'terminated', 'closed'].includes(a.status) &&
    (!search || a.name.toLowerCase().includes(search.toLowerCase()))
  );

  // ── Common table components ────────────────────────────────────────────────

  function EmptyRow({ cols }: { cols: number }) {
    return (
      <TableRow>
        <TableCell colSpan={cols} align="center">
          <Typography color="text.secondary" variant="body2" py={3}>No accounts found.</Typography>
        </TableCell>
      </TableRow>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={3} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <BusinessIcon color="primary" /> Account Management
      </Typography>

      {/* KPI cards — always visible */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={4}>
          <KpiCard icon={<CreditCardIcon />} label="Monthly Recurring Revenue" value={`$${(summary.total_mrr_cents / 100).toLocaleString()}/mo`} color="#2ecc71" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KpiCard icon={<BusinessIcon />} label="Partners in Trial" value={summary.trial_count} color="primary.main" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KpiCard icon={<WarningAmberIcon />} label="Past Due" value={summary.past_due_count} color="error.main" alert={summary.past_due_count > 0} />
        </Grid>
      </Grid>

      {msg   && <Alert severity="info"  sx={{ mb: 2 }} onClose={() => setMsg('')}>{msg}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 0, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="All Accounts" />
        <Tab label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            Expired Accounts
            {expired.length > 0 && <Chip label={expired.length} size="small" color="error" sx={{ height: 18, fontSize: 11 }} />}
          </Box>
        } />
        <Tab label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            Suspended / Closed
            {filteredSuspended.length > 0 && <Chip label={filteredSuspended.length} size="small" color="warning" sx={{ height: 18, fontSize: 11 }} />}
          </Box>
        } />
        <Tab label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            Recycle Bin
            {recycle.length > 0 && <Chip label={recycle.length} size="small" color="error" sx={{ height: 18, fontSize: 11 }} />}
          </Box>
        } />
      </Tabs>

      {/* ── Search bar ──────────────────────────────────────────────────────── */}
      <Box sx={{ py: 2 }}>
        <TextField
          placeholder="Search accounts…"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ width: 320 }}
        />
      </Box>

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* TAB 0 — All Accounts                                                */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      {tab === 0 && (
        loading ? <CircularProgress /> : (
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Account</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Billing</TableCell>
                  <TableCell>Trial End</TableCell>
                  <TableCell>Users</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAll.map((a) => (
                  <TableRow key={a.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" fontWeight={500}>{a.name}</Typography>
                        {(scanUsageToday[a.id] ?? 0) > 0 && (
                          <Chip
                            label={`Today: ${scanUsageToday[a.id]} scan${scanUsageToday[a.id] !== 1 ? 's' : ''}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontSize: '0.65rem', height: 18, '& .MuiChip-label': { px: 0.75 } }}
                          />
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">{a.organisation}</Typography>
                      <CopyUuid id={a.id} />
                    </TableCell>
                    <TableCell>
                      <Chip label={a.billing_plan || a.plan || 'starter'} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                    </TableCell>
                    <TableCell><AccountStatusChip status={a.status} /></TableCell>
                    <TableCell><BillingChip status={a.billing_status} /></TableCell>
                    <TableCell>
                      <Typography variant="caption" color={a.trial_end && new Date(a.trial_end) < new Date() ? 'error.main' : 'text.secondary'}>
                        {fmtDate(a.trial_end)}
                      </Typography>
                    </TableCell>
                    <TableCell>{a.users_count ?? 0}</TableCell>
                    <TableCell><Typography variant="caption" color="text.secondary">{fmtDate(a.created_at)}</Typography></TableCell>
                    <TableCell align="right">
                      <AccountRowMenu account={a} onAction={(action) => handleRowAction(action, a)} />
                    </TableCell>
                  </TableRow>
                ))}
                {filteredAll.length === 0 && <EmptyRow cols={8} />}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* TAB 1 — Expired Accounts                                            */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      {tab === 1 && (
        <>
          {/* Bulk action bar */}
          {selected.size > 0 && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2, p: 1.5, bgcolor: 'rgba(0,180,216,0.06)', borderRadius: 2, border: '1px solid rgba(0,180,216,0.2)' }}>
              <Typography variant="body2" sx={{ alignSelf: 'center', mr: 1 }}>
                {selected.size} selected
              </Typography>
              <Button
                size="small" variant="contained" startIcon={<EmailIcon />}
                onClick={() => setDialog({ type: 'send_email', accountIds: Array.from(selected), accountNames: `${selected.size} accounts` })}
              >
                Email Selected
              </Button>
              <Button
                size="small" variant="outlined"
                onClick={() => setDialog({ type: 'extend_trial', accounts: expired.filter((e) => selected.has(e.id)) as any, bulk: true })}
              >
                Extend Trial
              </Button>
              <Button size="small" onClick={() => setSelected(new Set())}>Clear</Button>
            </Box>
          )}

          {expLoading ? (
            <CircularProgress />
          ) : expired.length === 0 ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              No expired accounts. All partners are in good standing.
              <Button size="small" startIcon={<RefreshIcon />} onClick={fetchExpired} sx={{ ml: 2 }}>Refresh</Button>
            </Alert>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        indeterminate={selected.size > 0 && selected.size < expired.length}
                        checked={selected.size === expired.length && expired.length > 0}
                        onChange={() => toggleSelectAll(expired)}
                      />
                    </TableCell>
                    <TableCell>Account</TableCell>
                    <TableCell>Plan</TableCell>
                    <TableCell>Billing</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Expired Since</TableCell>
                    <TableCell>Days Expired</TableCell>
                    <TableCell>Users</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {expired.map((a) => (
                    <TableRow key={a.id} hover selected={selected.has(a.id)}>
                      <TableCell padding="checkbox">
                        <Checkbox size="small" checked={selected.has(a.id)} onChange={() => toggleSelect(a.id)} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{a.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{a.email}</Typography>
                        <CopyUuid id={a.id} />
                      </TableCell>
                      <TableCell>
                        <Chip label={a.plan} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                      </TableCell>
                      <TableCell><BillingChip status={a.billing_status} /></TableCell>
                      <TableCell><ReasonChip reason={a.reason} /></TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">{fmtDate(a.paused_since || a.trial_end)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}
                          color={a.paused_days > 20 ? 'error.main' : a.paused_days > 10 ? 'warning.main' : 'text.primary'}>
                          {a.paused_days}d
                        </Typography>
                      </TableCell>
                      <TableCell>{a.users_count}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Button size="small" variant="outlined" startIcon={<EmailIcon />}
                            onClick={() => setDialog({ type: 'send_email', accountIds: [a.id], accountNames: a.name })}>
                            Email
                          </Button>
                          <Button size="small" variant="outlined"
                            onClick={() => setDialog({ type: 'extend_trial', accounts: [a as any], bulk: false })}>
                            Extend Trial
                          </Button>
                          <Button size="small" variant="contained" color="success"
                            onClick={() => {
                              setDialog({ type: 'confirm', title: `Reactivate "${a.name}"?`, body: 'Restore full access for all users.', confirmLabel: 'Reactivate', onConfirm: () => doAction('reactivate', a.id, a.name) });
                            }}>
                            Reactivate
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* TAB 2 — Suspended / Closed                                          */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      {tab === 2 && (
        loading ? <CircularProgress /> :
        filteredSuspended.length === 0 ? (
          <Alert severity="success" sx={{ mt: 1 }}>No suspended or closed accounts.</Alert>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Account</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Billing</TableCell>
                  <TableCell>Trial End</TableCell>
                  <TableCell>Users</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSuspended.map((a) => (
                  <TableRow key={a.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{a.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{a.organisation}</Typography>
                      <CopyUuid id={a.id} />
                    </TableCell>
                    <TableCell>
                      <Chip label={a.billing_plan || a.plan || 'starter'} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                    </TableCell>
                    <TableCell><AccountStatusChip status={a.status} /></TableCell>
                    <TableCell><BillingChip status={a.billing_status} /></TableCell>
                    <TableCell><Typography variant="caption" color="text.secondary">{fmtDate(a.trial_end)}</Typography></TableCell>
                    <TableCell>{a.users_count ?? 0}</TableCell>
                    <TableCell><Typography variant="caption" color="text.secondary">{fmtDate(a.created_at)}</Typography></TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {a.status === 'closed'
                          ? <Button size="small" variant="contained" color="success" onClick={() => setDialog({ type: 'confirm', title: `Reopen "${a.name}"?`, body: 'Restore full access for all users.', confirmLabel: 'Reopen', onConfirm: () => doAction('reopen', a.id, a.name) })}>Reopen</Button>
                          : <Button size="small" variant="contained" color="success" onClick={() => setDialog({ type: 'confirm', title: `Reactivate "${a.name}"?`, body: 'Restore access for all users.', confirmLabel: 'Reactivate', onConfirm: () => doAction('reactivate', a.id, a.name) })}>Reactivate</Button>
                        }
                        <Button size="small" variant="outlined" startIcon={<EmailIcon />}
                          onClick={() => setDialog({ type: 'send_email', accountIds: [a.id], accountNames: a.name })}>
                          Email
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredSuspended.length === 0 && <EmptyRow cols={8} />}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* TAB 3 — Recycle Bin                                                  */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      {tab === 3 && (
        recLoading ? <CircularProgress /> :
        recycle.length === 0 ? (
          <Alert severity="success" sx={{ mt: 1 }}>Recycle bin is empty — no terminated accounts.</Alert>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Account</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Terminated On</TableCell>
                  <TableCell>Days Remaining</TableCell>
                  {isSuperAdmin && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {recycle.map((a) => (
                  <TableRow key={a.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{a.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{a.email}</Typography>
                      <CopyUuid id={a.id} />
                    </TableCell>
                    <TableCell><AccountStatusChip status={a.status} /></TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {fmtDate(a.deleted_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${a.days_remaining}d remaining`}
                        size="small"
                        color={a.days_remaining < 7 ? 'error' : a.days_remaining < 14 ? 'warning' : 'default'}
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    {isSuperAdmin && (
                      <TableCell align="right">
                        <Button
                          size="small" variant="contained" color="success"
                          onClick={() => setDialog({
                            type: 'confirm',
                            title: `Restore "${a.name}"?`,
                            body: `This will restore the account and reactivate all users. You have ${a.days_remaining} days remaining in the recovery window.`,
                            confirmLabel: 'Restore',
                            onConfirm: () => doAction('restore', a.id, a.name),
                          })}
                        >
                          Restore
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}

      {/* ── Dialogs ──────────────────────────────────────────────────────────── */}
      {dialog?.type === 'confirm' && (
        <ConfirmDlg d={dialog} onClose={() => setDialog(null)} />
      )}
      {dialog?.type === 'extend_trial' && (
        <ExtendTrialDlg d={dialog} onClose={() => setDialog(null)} onDone={(m) => { setMsg(m); fetchAccounts(); if (tab === 1) fetchExpired(); }} />
      )}
      {dialog?.type === 'send_email' && (
        <SendEmailDlg d={dialog} onClose={() => setDialog(null)} onDone={(m) => setMsg(m)} />
      )}
      {dialog?.type === 'reset_passwords' && (
        <ResetPasswordsDlg
          d={dialog}
          onClose={() => setDialog(null)}
          onAction={(step, results) =>
            setDialog((prev) => prev?.type === 'reset_passwords' ? { ...prev, step, results: results ?? [] } : prev)
          }
        />
      )}
    </Box>
  );
}
